import React, { useState } from 'react';
import { getAllMockPitchers } from '../utils/mockPlayerNames';
import { SCOUTING_REPORTS } from '../data/scoutingReports';

// UI Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-lg ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-4 ${className}`}>
    {children}
  </div>
);

const StyledDropdown = ({ value, onChange, options, placeholder = "Select Pitcher" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const listRef = React.useRef(null);
  
  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
  };

  // Check if content is scrollable when dropdown opens
  React.useEffect(() => {
    if (isOpen && listRef.current) {
      const element = listRef.current;
      const hasScroll = element.scrollHeight > element.clientHeight;
      setShowScrollIndicator(hasScroll);
      
      // Hide scroll indicator when user scrolls to bottom
      const handleScroll = () => {
        const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 5;
        setShowScrollIndicator(hasScroll && !isAtBottom);
      };
      
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, options.length]);

  return (
    <div className="relative w-full max-w-xl">
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-transparent border-2 border-yellow-400 rounded-xl text-white text-left flex items-center justify-between hover:bg-yellow-400/10 transition-colors duration-200"
      >
        <span className="text-base font-medium text-white">
          {value || placeholder}
        </span>
        <svg 
          className={`w-4 h-4 text-white transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          ref={listRef}
          className="absolute top-full left-0 right-0 mt-2 bg-slate-800/95 backdrop-blur-sm border border-white/20 rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto"
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className="w-full px-4 py-3 text-left text-white hover:bg-yellow-400/20 transition-colors duration-150 border-b border-white/5 last:border-b-0"
            >
              <div className="text-base font-medium text-white">{option.label}</div>
            </button>
          ))}
          
          {/* Sticky Scroll Indicator - Fixed at bottom of visible area */}
          {showScrollIndicator && (
            <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-slate-800 via-slate-800/80 to-transparent pt-2 pb-2 pointer-events-none">
              <div className="flex justify-center">
                <div className="bg-yellow-400/95 text-slate-900 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 shadow-lg animate-pulse">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  Scroll for more players
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// Grade Bar Component with Present/Future grades
const CompactGradeBar = ({ label, presentGrade, futureGrade, maxGrade = 80 }) => {
  const presentPercentage = (presentGrade / maxGrade) * 100;
  const futurePercentage = (futureGrade / maxGrade) * 100;
  
  const getColor = (grade) => {
    if (grade >= 70) return 'bg-green-500';
    if (grade >= 60) return 'bg-blue-500';
    if (grade >= 50) return 'bg-yellow-500';
    if (grade >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getBorderColor = (grade) => {
    if (grade >= 70) return 'border-green-500';
    if (grade >= 60) return 'border-blue-500';
    if (grade >= 50) return 'border-yellow-500';
    if (grade >= 40) return 'border-orange-500';
    return 'border-red-500';
  };

  return (
    <div className="mb-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-medium text-white">{label} {presentGrade}/{futureGrade}</span>
        <span className="text-xs font-bold text-yellow-400">{futureGrade}</span>
      </div>
      <div className="w-full bg-slate-600 rounded-full h-2 relative">
        {/* Present grade - filled bar */}
        <div 
          className={`h-2 rounded-full ${getColor(presentGrade)}`}
          style={{ width: `${presentPercentage}%` }}
        ></div>
        {/* Future grade - ghost outline bar */}
        <div 
          className={`absolute top-0 h-2 rounded-full border-2 ${getBorderColor(futureGrade)} bg-transparent opacity-50`}
          style={{ 
            width: `${futurePercentage}%`,
            left: '0'
          }}
        ></div>
      </div>
    </div>
  );
};

export default function PitcherScoutingReports() {
  const mockPitchers = getAllMockPitchers();
  const [selectedPitcher, setSelectedPitcher] = useState(mockPitchers[0] || '');

  // Get real scouting report data
  const getScoutingReportData = (name) => {
    // Convert name to slug format to match our data keys
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const report = SCOUTING_REPORTS[slug];
    
    if (!report) return null;
    
    return {
      basicInfo: {
        age: report.ageSize?.split(' • ')[0] || '22',
        height: report.ageSize?.split(' • ')[1] || "6'0\"",
        weight: report.ageSize?.split(' • ')[2]?.split(' ')[0] || '180',
        throws: report.ageSize?.split(' • ')[3] || 'R',
        position: report.ageSize?.split(' • ')[3] === 'L' ? 'LHP' : 'RHP',
        experience: 'Draft Eligible'
      },
      grades: {
        fastball: report.pitches?.fourSeam || { present: 50, future: 50 },
        slider: report.pitches?.slider || { present: 45, future: 45 },
        changeup: report.pitches?.changeup || { present: 45, future: 45 },
        curveball: report.pitches?.curveball || { present: 40, future: 40 },
        command: report.tools?.command || { present: 45, future: 45 },
        control: report.tools?.control || { present: 40, future: 40 },
        overall: report.overallFV || 45
      },
      stats: {
        era: parseFloat(report.stats?.split(' | ')[0]?.replace(' ERA', '')) || 4.00,
        whip: parseFloat(report.stats?.split(' | ')[1]?.replace(' WHIP', '')) || 1.35,
        k9: parseFloat(report.stats?.split(' | ')[2]?.replace(' K/9', '')) || 8.0,
        bb9: parseFloat(report.stats?.split(' | ')[3]?.replace(' BB/9', '')) || 3.5,
        hr9: 1.0,
        babip: 0.300,
        fip: 4.00,
        xfip: 4.00
      },
      summary: report.summary || 'Scouting report in development.',
      strengths: [report.keyStrengths || 'Developing skills'],
      concerns: [report.mainConcerns || 'Areas for improvement'],
      development: [report.developmentFocus || 'Continue development'],
      projection: {
        role: report.roleProjection || 'Organizational Depth',
        eta: '2026-2027',
        risk: report.risk || 'Medium'
      }
    };
  };

  // Generate pitcher data function (fallback for pitchers not in our data)
  const generatePitcherData = (name) => {
    // Key pitchers with specific data
    const keyPitchers = {
      'Carlos Alvarez': {
        basicInfo: { age: 22, height: "6'0\"", weight: 175, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 50, future: 55 }, 
          slider: { present: 45, future: 50 }, 
          changeup: { present: 45, future: 50 }, 
          curveball: { present: 40, future: 45 }, 
          command: { present: 45, future: 50 }, 
          control: { present: 45, future: 50 }, 
          overall: 50 
        },
        stats: { era: 3.12, whip: 1.22, k9: 10.4, bb9: 2.8, hr9: 0.7, babip: .285, fip: 2.98, xfip: 3.05 },
        summary: "Athletic RHP with sinker-heavy approach and improving command. Heavy sinker 90-94 T95 generates ground balls and weak contact. SL shows solid break at 82-85 with late bite. CH is developing at 85-88 with good fade vs. LHH. CB shows promise with 11-5 break. Command has improved significantly. Ground ball specialist with starter potential.",
        strengths: ["Heavy sinker generates ground balls", "Competitive makeup", "Improving command"],
        concerns: ["Command consistency", "Secondary pitch development"],
        development: ["Refine slider command", "Improve changeup fade"],
        projection: { role: "Mid-rotation starter", eta: "2026-2027" }
      },
      'Miguel Acosta': {
        basicInfo: { age: 22, height: "6'3\"", weight: 205, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 60, future: 70 }, 
          slider: { present: 45, future: 50 }, 
          changeup: { present: 40, future: 45 }, 
          curveball: { present: 35, future: 40 }, 
          command: { present: 30, future: 40 }, 
          control: { present: 30, future: 40 }, 
          overall: 45 
        },
        stats: { era: 2.85, whip: 1.15, k9: 11.8, bb9: 2.8, hr9: 0.6, babip: .285, fip: 2.95, xfip: 3.05 },
        summary: "Premium arm with plus-plus velocity but significant command concerns. FB sits 95-98 T100 with heavy life and ride through the zone - true 70-grade heater. SL flashes average with tight break but inconsistent release point. CH shows promise with good arm speed but needs refinement. Delivery has effort and timing issues leading to below-average command. High-ceiling arm that could go anywhere from 1st round to undrafted depending on team's risk tolerance.",
        strengths: ["Elite velocity", "Premium arm strength"],
        concerns: ["Well below-average command", "Effort in delivery"],
        development: ["Major delivery overhaul", "Improve timing"],
        projection: { role: "Starting pitcher", eta: "2025-2027" }
      },
      'Alex Blair': {
        basicInfo: { age: 20, height: "6'0\"", weight: 175, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 55, future: 60 }, 
          slider: { present: 50, future: 55 }, 
          changeup: { present: 55, future: 60 }, 
          curveball: { present: 45, future: 50 }, 
          command: { present: 55, future: 60 }, 
          control: { present: 55, future: 60 }, 
          overall: 55 
        },
        stats: { era: 2.95, whip: 1.18, k9: 10.8, bb9: 2.2, hr9: 0.8, babip: .285, fip: 2.85, xfip: 3.02 },
        summary: "Athletic RHP with above-average stuff and advanced command. FB sits 93-97 T98 with good life and command. CH is plus offering with excellent arm speed and late fade - legitimate out pitch vs. RHH. SL shows consistent bite at 85-87. Exceptional feel for pitching and sequencing. Solid starter profile with good velocity and command combination.",
        strengths: ["Above-average velocity", "Plus changeup", "Advanced command/control"],
        concerns: ["Needs to maintain stuff vs. higher levels"],
        development: ["Continue command refinement", "Maintain velocity gains"],
        projection: { role: "Mid-rotation starter", eta: "2025-2026" }
      },
      'David Bennett': {
        basicInfo: { age: 23, height: "6'2\"", weight: 190, throws: 'L', position: 'LHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 40, future: 45 }, 
          slider: { present: 35, future: 40 }, 
          changeup: { present: 35, future: 40 }, 
          curveball: { present: 45, future: 50 }, 
          command: { present: 40, future: 45 }, 
          control: { present: 35, future: 40 }, 
          overall: 40 
        },
        stats: { era: 4.15, whip: 1.42, k9: 7.8, bb9: 3.8, hr9: 1.3, babip: .315, fip: 3.95, xfip: 3.88 },
        summary: "Left-handed college pitcher with average stuff and developing secondary offerings. Fastball sits 86-89 mph with natural arm-side run. Curveball is his best pitch with good 12-6 break. Changeup and slider need work. Command is average for amateur level. Left-handed pitching gives him draft appeal despite modest stuff.",
        strengths: ["Left-handed pitcher", "Good curveball shape"],
        concerns: ["Below-average fastball", "Inconsistent secondaries"],
        development: ["Add velocity", "Improve CH consistency"],
        projection: { role: "Back-end starter", eta: "2027-2028" }
      },
      'Chris Clark': {
        basicInfo: { age: 24, height: "6'4\"", weight: 210, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 40, future: 45 }, 
          slider: { present: 35, future: 40 }, 
          changeup: { present: 35, future: 40 }, 
          curveball: { present: 30, future: 35 }, 
          command: { present: 30, future: 35 }, 
          control: { present: 30, future: 35 }, 
          overall: 35 
        },
        stats: { era: 4.68, whip: 1.52, k9: 7.2, bb9: 4.4, hr9: 1.5, babip: .325, fip: 4.45, xfip: 4.25 },
        summary: "Large-framed RHP with well below-average stuff and poor command. Age (24) and inability to throw strikes consistently make him an undraftable prospect. Lacks the feel and athleticism to project as a professional pitcher.",
        strengths: ["Size/frame only"],
        concerns: ["Poor command/control", "Below-average stuff", "Age", "High walk rates"],
        development: ["Unlikely professional future"],
        projection: { role: "Undraftable", eta: "N/A" }
      },
      'Ryan Dean': {
        basicInfo: { age: 22, height: "6'2\"", weight: 195, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 45, future: 50 }, 
          slider: { present: 45, future: 50 }, 
          changeup: { present: 35, future: 40 }, 
          curveball: { present: 35, future: 40 }, 
          command: { present: 40, future: 45 }, 
          control: { present: 35, future: 40 }, 
          overall: 40 
        },
        stats: { era: 3.28, whip: 1.31, k9: 9.8, bb9: 3.2, hr9: 0.9, babip: .298, fip: 3.45, xfip: 3.38 },
        summary: "Athletic RHP with projectable frame and developing stuff. FB 89-92 T94 shows good life. SL flashes above-average with sharp break - best secondary. CB has 11-5 shape with depth. CH needs refinement. Command below-average but trending up. Back-end starter potential if command improves.",
        strengths: ["Above-average SL", "Athletic build"],
        concerns: ["Command consistency", "CH development"],
        development: ["Improve FB command", "Develop CH"],
        projection: { role: "Back-end starter", eta: "2026-2027" }
      },
      'Josh Doyle': {
        basicInfo: { age: 21, height: "5'11\"", weight: 170, throws: 'L', position: 'LHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 40, future: 45 }, 
          slider: { present: 45, future: 50 }, 
          changeup: { present: 45, future: 50 }, 
          curveball: { present: 40, future: 45 }, 
          command: { present: 45, future: 50 }, 
          control: { present: 45, future: 50 }, 
          overall: 45 
        },
        stats: { era: 3.05, whip: 1.24, k9: 9.5, bb9: 2.8, hr9: 0.8, babip: .288, fip: 3.18, xfip: 3.25 },
        summary: "Undersized LHP with advanced pitchability and feel. FB 84-87 T88 with natural ASR and sink. Plus CH with excellent arm speed and fade - true out pitch vs. RHH. SL shows average potential. CB developing with 12-6 break. Command well above-average for level. Classic finesse lefty profile.",
        strengths: ["Plus changeup", "Advanced command"],
        concerns: ["Below-average velocity", "Size limitations"],
        development: ["Maintain command advantage", "Continue CH refinement"],
        projection: { role: "Back-end starter", eta: "2026-2027" }
      },
      'Trevor Evans': {
        basicInfo: { age: 23, height: "6'3\"", weight: 200, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 45, future: 50 }, 
          slider: { present: 40, future: 45 }, 
          changeup: { present: 40, future: 45 }, 
          curveball: { present: 35, future: 40 }, 
          command: { present: 35, future: 40 }, 
          control: { present: 30, future: 35 }, 
          overall: 35 
        },
        stats: { era: 3.85, whip: 1.38, k9: 8.7, bb9: 3.6, hr9: 1.2, babip: .308, fip: 3.92, xfip: 3.88 },
        summary: "Large-framed RHP with solid velocity but command concerns. FB sits 91-94 T96 with heavy sink and occasional cut. SL flashes average with tight break but inconsistent shape. CH shows promise with good arm speed. Delivery has some effort leading to below-average command. Age and command issues limit draft appeal despite solid stuff.",
        strengths: ["Good velocity", "Heavy sinker action"],
        concerns: ["Below-average command", "Inconsistent breaking balls"],
        development: ["Major command overhaul", "Simplify delivery"],
        projection: { role: "Middle reliever", eta: "2027-2028" }
      },
      'Jake Ellis': {
        basicInfo: { age: 22, height: "6'2\"", weight: 195, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 50, future: 50 }, 
          slider: { present: 45, future: 45 }, 
          changeup: { present: 45, future: 45 }, 
          curveball: { present: 45, future: 45 }, 
          command: { present: 45, future: 45 }, 
          control: { present: 40, future: 40 }, 
          overall: 45 
        },
        stats: { era: 3.58, whip: 1.34, k9: 8.8, bb9: 3.4, hr9: 1.0, babip: .295, fip: 3.45, xfip: 3.52 },
        summary: "Solid RHP with usable velocity and developing pitchability. FB 89–92 T94 shows late life. SL flashes average tilt, CH maintains arm speed with occasional fade. Still fringy command, but trending upward. Profiles as a reliable org depth arm with a chance to carve a bullpen role if secondaries firm up.",
        strengths: ["Good FB life", "Developing 3-pitch mix"],
        concerns: ["Command consistency", "Breaking balls need work"],
        development: ["Improve command/control", "Continue breaking ball development"],
        projection: { role: "Organizational depth", eta: "2026-2027" }
      },
      'Blake Kane': {
        basicInfo: { age: 20, height: "6'0\"", weight: 175, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 40, future: 40 }, 
          slider: { present: 35, future: 35 }, 
          changeup: { present: 35, future: 35 }, 
          curveball: { present: 35, future: 35 }, 
          command: { present: 35, future: 35 }, 
          control: { present: 30, future: 30 }, 
          overall: 35 
        },
        stats: { era: 4.85, whip: 1.65, k9: 6.2, bb9: 4.9, hr9: 1.3, babip: .325, fip: 4.95, xfip: 4.88 },
        summary: "Young RHP with well below-average stuff across the board. FB 84-87 mph is too light for professional baseball. Poor command and control make him a long-shot development project at best. Age is only positive attribute.",
        strengths: ["Young age (20)"],
        concerns: ["Well below-average velocity", "Poor command", "Light frame", "No feel for breaking balls"],
        development: ["Extreme long-shot. Needs major physical and mechanical gains."],
        projection: { role: "Organizational depth", eta: "Unlikely" }
      },
      'Codi Knight': {
        basicInfo: { age: 21, height: "6'1\"", weight: 180, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 40, future: 40 }, 
          slider: { present: 35, future: 35 }, 
          changeup: { present: 40, future: 40 }, 
          curveball: { present: 35, future: 35 }, 
          command: { present: 35, future: 35 }, 
          control: { present: 30, future: 30 }, 
          overall: 35 
        },
        stats: { era: 4.78, whip: 1.58, k9: 6.9, bb9: 4.6, hr9: 1.2, babip: .318, fip: 4.85, xfip: 4.72 },
        summary: "Lean RHP with below-average present stuff but some projection. FB 85–88 T89 comes in flat. Breaking ball shapes inconsistent, often backed-up. CH flashes occasional separation but not reliable. Walks pile up with scattered command. Needs strength gains and delivery refinement to create a pro path.",
        strengths: ["Young age (21)", "Some projection remaining"],
        concerns: ["Below-average velo", "Poor command/control"],
        development: ["Major command overhaul", "Simplify delivery"],
        projection: { role: "Deep development", eta: "Unknown" }
      },
      'Preston Lewis': {
        basicInfo: { age: 22, height: "6'2\"", weight: 190, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 45, future: 45 }, 
          slider: { present: 40, future: 40 }, 
          changeup: { present: 45, future: 45 }, 
          curveball: { present: 40, future: 40 }, 
          command: { present: 40, future: 40 }, 
          control: { present: 35, future: 35 }, 
          overall: 40 
        },
        stats: { era: 4.25, whip: 1.48, k9: 7.8, bb9: 4.1, hr9: 1.1, babip: .308, fip: 4.35, xfip: 4.28 },
        summary: "College RHP with fringe velocity (87–90 T91) and inconsistent execution. FB shows little life, SL lacks bite, CH flashes but drifts arm-side. Below-average command keeps profile limited. Fits best as an org depth type who can eat innings but lacks impact traits.",
        strengths: ["Decent size", "Some college experience"],
        concerns: ["Fringe velo", "Poor command/control"],
        development: ["Major command overhaul", "Improve delivery consistency"],
        projection: { role: "Organizational depth", eta: "2027-2028" }
      },
      'Jackson Long': {
        basicInfo: { age: 23, height: "6'0\"", weight: 175, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 40, future: 40 }, 
          slider: { present: 35, future: 35 }, 
          changeup: { present: 40, future: 40 }, 
          curveball: { present: 35, future: 35 }, 
          command: { present: 35, future: 35 }, 
          control: { present: 30, future: 30 }, 
          overall: 35 
        },
        stats: { era: 4.95, whip: 1.62, k9: 6.5, bb9: 4.8, hr9: 1.4, babip: .322, fip: 5.05, xfip: 4.95 },
        summary: "Righty with below-average stuff and command issues. FB 85–88 T89 plays flat. Secondaries lack bite or separation. High walk totals and hittability limit upside. Competitive makeup shows, but metrics point to organizational filler without notable projection left.",
        strengths: ["Some college experience", "Competitive makeup"],
        concerns: ["Below-average velo", "Poor command/control"],
        development: ["Major command overhaul", "Simplify delivery"],
        projection: { role: "Organizational filler", eta: "Unknown" }
      },
      'Drew Marsh': {
        basicInfo: { age: 24, height: "5'10\"", weight: 170, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 35, future: 35 }, 
          slider: { present: 40, future: 40 }, 
          changeup: { present: 35, future: 35 }, 
          curveball: { present: 35, future: 35 }, 
          command: { present: 35, future: 35 }, 
          control: { present: 30, future: 30 }, 
          overall: 35 
        },
        stats: { era: 5.45, whip: 1.72, k9: 5.8, bb9: 5.2, hr9: 1.6, babip: .335, fip: 5.55, xfip: 5.42 },
        summary: "Undersized RHP with limited present tools. FB 83–86 T87 is light, secondaries lack finish, and command is well below average. Struggles to miss bats or throw strikes consistently. Competitive, but physical limitations and performance indicators suggest a conversion candidate or non-prospect.",
        strengths: ["Competitive spirit", "Some college experience"],
        concerns: ["Well below-average velo", "Poor command/control"],
        development: ["Consider position change", "Major velocity gains needed"],
        projection: { role: "Non-prospect", eta: "Unknown" }
      },
      'Connor Mason': {
        basicInfo: { age: 22, height: "6'3\"", weight: 205, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 55, future: 55 }, 
          slider: { present: 50, future: 50 }, 
          changeup: { present: 45, future: 45 }, 
          curveball: { present: 45, future: 45 }, 
          command: { present: 45, future: 45 }, 
          control: { present: 40, future: 40 }, 
          overall: 50 
        },
        stats: { era: 3.42, whip: 1.33, k9: 9.5, bb9: 3.2, hr9: 0.9, babip: .288, fip: 3.35, xfip: 3.42 },
        summary: "Physical RHP with pro body and real velocity. FB 91–94 T96 carries through the zone with late hop and occasional cut. SL flashes sharp bite, CH developing with solid arm speed. Command is loose but improving. Size, velocity, and raw stuff give him mid-round draft appeal if consistency sharpens.",
        strengths: ["Good velocity (91–94)", "Large projectable frame"],
        concerns: ["Below-average command", "Inconsistent secondaries"],
        development: ["Improve command/control", "Continue slider development"],
        projection: { role: "Starting pitcher", eta: "2025-2026" }
      },
      'Miles Newman': {
        basicInfo: { age: 23, height: "6'1\"", weight: 185, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 50, future: 50 }, 
          slider: { present: 45, future: 45 }, 
          changeup: { present: 45, future: 45 }, 
          curveball: { present: 45, future: 45 }, 
          command: { present: 45, future: 45 }, 
          control: { present: 40, future: 40 }, 
          overall: 45 
        },
        stats: { era: 3.68, whip: 1.36, k9: 8.9, bb9: 3.3, hr9: 1.0, babip: .298, fip: 3.75, xfip: 3.68 },
        summary: "Solid RHP with playable stuff across the board. FB 89–92 T94 shows some late life, SL flashes average with occasional depth, CH comes with arm speed and fade. Below-average command, but showing improvement. Age relative to class caps projection some, yet he has enough to be a potential org arm with depth value.",
        strengths: ["Good fastball life", "Developing three-pitch mix"],
        concerns: ["Age for draft class", "Command consistency"],
        development: ["Improve command/control", "Continue breaking ball development"],
        projection: { role: "Organizational depth", eta: "2026-2027" }
      },
      'Kyle Nicholson': {
        basicInfo: { age: 21, height: "6'2\"", weight: 190, throws: 'R', position: 'RHP', experience: 'Draft Eligible' },
        grades: { 
          fastball: { present: 50, future: 50 }, 
          slider: { present: 50, future: 50 }, 
          changeup: { present: 50, future: 50 }, 
          curveball: { present: 45, future: 45 }, 
          command: { present: 50, future: 50 }, 
          control: { present: 45, future: 45 }, 
          overall: 50 
        },
        stats: { era: 3.25, whip: 1.29, k9: 9.4, bb9: 2.9, hr9: 0.8, babip: .285, fip: 3.18, xfip: 3.25 },
        summary: "Balanced RHP with clean three-pitch mix and pitchability. FB 88–91 T93 shows some late run, SL flashes average tilt, CH shows developing fade. Delivery repeats and command is near average. Lacks big physical projection, but makeup and arsenal quality suggest future org starter who could push higher with velocity gains.",
        strengths: ["Balanced 3-pitch mix", "Good pitching feel"],
        concerns: ["Needs velocity gains", "Limited projection"],
        development: ["Add 2–3 mph velocity", "Continue slider development"],
        projection: { role: "Back-end starter", eta: "2025-2026" }
      }
    };

    // Return predefined data if available, otherwise generate generic data
    if (keyPitchers[name]) {
      return keyPitchers[name];
    }

    // Generate realistic data for other pitchers
    const ages = [19, 20, 21, 22, 23, 24, 25];
    const heights = ["5'11\"", "6'0\"", "6'1\"", "6'2\"", "6'3\"", "6'4\""];
    const weights = [170, 175, 180, 185, 190, 195, 200, 205, 210];
    const throws = ['R', 'L'];
    const experiences = ['1st Year Pro', '2nd Year Pro', '3rd Year Pro', '4th Year Pro'];
    
    const hash = name.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
    const seed = Math.abs(hash);
    
    return {
      basicInfo: {
        age: ages[seed % ages.length],
        height: heights[seed % heights.length],
        weight: weights[seed % weights.length],
        throws: throws[seed % throws.length],
        position: throws[seed % throws.length] === 'L' ? 'LHP' : 'RHP',
        experience: experiences[seed % experiences.length]
      },
      grades: {
        fastball: { present: 40, future: 45 },
        slider: { present: 40, future: 45 },
        changeup: { present: 40, future: 45 },
        curveball: { present: 40, future: 45 },
        command: { present: 40, future: 45 },
        control: { present: 40, future: 45 },
        overall: 40
      },
      stats: { era: 4.50, whip: 1.40, k9: 7.5, bb9: 3.5, hr9: 1.2, babip: .300, fip: 4.20, xfip: 4.30 },
      summary: "Organizational arm with fringe stuff and developing feel for pitching. Fastball sits 88-91 mph with average life. Secondary offerings show flashes but need refinement and consistency. Command is below-average but improving with experience. Athletic frame with room for physical development. Projects as organizational depth with potential to contribute in middle relief role if development continues.",
      strengths: ["Good work ethic", "Coachable attitude"],
      concerns: ["Needs development", "Command consistency"],
      development: ["Improve command/control", "Continue pitch development"],
      projection: { role: "Organizational depth", eta: "2027-2028" }
    };
  };

  const currentPlayerData = selectedPitcher ? (getScoutingReportData(selectedPitcher) || generatePitcherData(selectedPitcher)) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* HEADER */}
      <div className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <h1 className="text-2xl font-bold tracking-tight text-white">⚡ Pitcher Scouting Reports</h1>
          <div className="text-sm text-white/70">
            Comprehensive scouting reports and player evaluations
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="mx-auto max-w-7xl px-6 pb-8">
        {/* CONDENSED LAYOUT - Everything on one screen */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          {/* LEFT COLUMN - Player Selection */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-bold text-white mb-3">Player Selection</h2>
            <StyledDropdown
              value={selectedPitcher}
              onChange={(e) => setSelectedPitcher(e.target.value)}
              options={mockPitchers.map(p => ({ value: p, label: p }))}
              placeholder="Select Pitcher"
            />
          </div>

          {/* RIGHT COLUMNS - Player Data */}
          {selectedPitcher && currentPlayerData && (
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-4">
                  {/* COMPACT HEADER */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-1">{selectedPitcher}</h2>
                      <div className="text-sm text-white/60">
                        {currentPlayerData.basicInfo.age} • {currentPlayerData.basicInfo.height} • {currentPlayerData.basicInfo.weight} lbs • {currentPlayerData.basicInfo.throws}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white/60 mb-1">FV</div>
                      <div className="text-2xl font-bold text-yellow-400">{currentPlayerData.grades.overall}</div>
                    </div>
                  </div>

                  {/* COMPACT STATS ROW */}
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentPlayerData.stats.era.toFixed(2)}</div>
                      <div className="text-sm text-white/60">ERA</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentPlayerData.stats.whip.toFixed(2)}</div>
                      <div className="text-sm text-white/60">WHIP</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentPlayerData.stats.k9.toFixed(1)}</div>
                      <div className="text-sm text-white/60">K/9</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentPlayerData.stats.bb9.toFixed(1)}</div>
                      <div className="text-sm text-white/60">BB/9</div>
                    </div>
                  </div>

                  {/* COMPACT PITCH GRADES */}
                  <div className="mb-4">
                    <h3 className="text-lg font-bold mb-3">Pitch Arsenal (Present/Future)</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <CompactGradeBar 
                        label="Fastball" 
                        presentGrade={currentPlayerData.grades.fastball?.present || currentPlayerData.grades.fastball} 
                        futureGrade={currentPlayerData.grades.fastball?.future || currentPlayerData.grades.fastball} 
                      />
                      <CompactGradeBar 
                        label="Command" 
                        presentGrade={currentPlayerData.grades.command?.present || currentPlayerData.grades.command} 
                        futureGrade={currentPlayerData.grades.command?.future || currentPlayerData.grades.command} 
                      />
                      <CompactGradeBar 
                        label="Slider" 
                        presentGrade={currentPlayerData.grades.slider?.present || currentPlayerData.grades.slider} 
                        futureGrade={currentPlayerData.grades.slider?.future || currentPlayerData.grades.slider} 
                      />
                      <CompactGradeBar 
                        label="Control" 
                        presentGrade={currentPlayerData.grades.control?.present || currentPlayerData.grades.control} 
                        futureGrade={currentPlayerData.grades.control?.future || currentPlayerData.grades.control} 
                      />
                      <CompactGradeBar 
                        label="Changeup" 
                        presentGrade={currentPlayerData.grades.changeup?.present || currentPlayerData.grades.changeup} 
                        futureGrade={currentPlayerData.grades.changeup?.future || currentPlayerData.grades.changeup} 
                      />
                      <CompactGradeBar 
                        label="Curveball" 
                        presentGrade={currentPlayerData.grades.curveball?.present || currentPlayerData.grades.curveball} 
                        futureGrade={currentPlayerData.grades.curveball?.future || currentPlayerData.grades.curveball} 
                      />
                    </div>
                  </div>

                  {/* COMPACT SCOUTING SUMMARY */}
                  <div className="mb-4">
                    <h3 className="text-lg font-bold mb-3">Scouting Summary</h3>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <p className="text-sm text-white/90 leading-relaxed">{currentPlayerData.summary}</p>
                    </div>
                  </div>

                  {/* COMPACT BOTTOM ROW */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-white">Key Strengths</h4>
                      <div className="space-y-1.5">
                        {currentPlayerData.strengths.slice(0, 2).map((strength, index) => (
                          <div key={index} className="text-sm text-white bg-slate-700/30 rounded p-2.5">
                            {strength}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-white">Main Concerns</h4>
                      <div className="space-y-1.5">
                        {currentPlayerData.concerns.slice(0, 2).map((concern, index) => (
                          <div key={index} className="text-sm text-white bg-slate-700/30 rounded p-2.5">
                            {concern}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* EMPTY STATE - Shows when no pitcher selected */}
        {!selectedPitcher && (
          <section className="mt-6">
            <Card>
              <CardContent>
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📋</div>
                  <h2 className="text-xl font-semibold mb-2">Select a Pitcher</h2>
                  <p className="text-white/60">Choose a pitcher from the dropdown above to view their comprehensive scouting report.</p>
                </div>
              </CardContent>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
}
